import type { ColDef } from "ag-grid-community";
import { type CustomFilterProps, useGridFilter } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type {
  BackendNumberFilterModel,
  BackendTextFilterModel,
} from "src/pages/Merchflows/store/agGridModelMapper";
import { Color } from "src/elements";

type BackendInputFilterModel = BackendTextFilterModel | BackendNumberFilterModel;
type BackendInputMode = "text" | "number";

type Props = CustomFilterProps<unknown, unknown, BackendInputFilterModel>;

type BackendInputFilterParams = {
  inputType?: BackendInputMode;
  placeholder?: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
  padding: 8px;
`;

const Input = styled.input`
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  padding: 6px 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: end;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  background: ${Color.white};
  padding: 4px 10px;
  cursor: pointer;
`;

const getFilterParams = (colDef: ColDef): BackendInputFilterParams => {
  return (colDef.filterParams as BackendInputFilterParams | undefined) || {};
};

const modelToDraftValue = (model: BackendInputFilterModel | null | undefined) => {
  if (!model) {
    return "";
  }

  return String(model.value);
};

export const BackendInputFilter = ({ model, onModelChange, colDef }: Props) => {
  const filterParams = useMemo(() => getFilterParams(colDef), [colDef]);
  const inputType = filterParams.inputType || "text";
  const [draftValue, setDraftValue] = useState(modelToDraftValue(model));

  useEffect(() => {
    setDraftValue(modelToDraftValue(model));
  }, [model]);

  useGridFilter({
    doesFilterPass: () => true,
    getModelAsString: (currentModel) => modelToDraftValue(currentModel as BackendInputFilterModel),
  });

  const apply = () => {
    if (draftValue === "") {
      onModelChange(null);
      return;
    }

    if (inputType === "number") {
      const parsedValue = Number(draftValue);

      if (!Number.isFinite(parsedValue)) {
        onModelChange(null);
        return;
      }

      onModelChange({
        filterType: "backendNumberInput",
        value: parsedValue,
      });
      return;
    }

    onModelChange({
      filterType: "backendTextInput",
      value: draftValue,
    });
  };

  const clear = () => {
    setDraftValue("");
    onModelChange(null);
  };

  return (
    <Container>
      <Input
        type={inputType}
        value={draftValue}
        placeholder={filterParams.placeholder}
        onChange={(event) => setDraftValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            apply();
          }
        }}
      />

      <Actions>
        <ActionButton type="button" onClick={clear}>
          Clear
        </ActionButton>

        <ActionButton type="button" onClick={apply}>
          Apply
        </ActionButton>
      </Actions>
    </Container>
  );
};
